/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the component that made the mutation, or from the cart
 *   drawer scope when an app made it.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 *
 * ⚠️ This file deliberately imports nothing. `configure()` is first-call-wins — a later
 * call returns false and changes nothing — and this module script races whatever
 * `{{ content_for_header }}` injects. A static import would hold `init()` until the
 * imported graph resolved, which for the cart graph means a CDN round trip for
 * `@shopify/events`. So the mutation origin below is pushed IN by
 * `assets/cart-actions.js` rather than pulled in from it.
 */

/** @type {EventTarget | null} */
let mutationOrigin = null;

/**
 * Records which component's mutation is in flight, so its Standard Event is emitted from
 * that component rather than from the drawer.
 *
 * Called by `assets/cart-actions.js` around each theme mutation — see the "EXACTLY ONE
 * STANDARD EVENT PER MUTATION" note there for why `event.target` has to keep meaning what
 * it meant before the mutations moved onto the action.
 *
 * @param {EventTarget | null} origin
 */
export function setMutationOrigin(origin) {
  mutationOrigin = origin;
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    // A theme mutation emits from the component that made it, because several listeners
    // identify their own mutation by `event.target` and would otherwise act on it twice:
    // `component-cart-items.js` skips `event.target === this`, and `cart-drawer.js`
    // reads `event.target.closest('dialog:modal')` to hold its auto-open until the
    // quick-add dialog has closed and restored focus.
    // An app's mutation sets no origin and keeps Horizon's drawer scope.
    eventTarget: () => mutationOrigin ?? document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
